import { useState, useRef, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Send, Mail, User, MapPin, MessageSquare, Loader2, ShieldCheck, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from 'sonner';
import axios from 'axios';

const MASCOT_URL = "https://customer-assets.emergentagent.com/job_b35da085-09af-4a55-863c-9eb3e423a8d1/artifacts/erel0x7n_buzon.png";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Orden principal: Venezuela, Colombia, Panamá, República Dominicana
const destinations = [
  { value: 'venezuela', label: 'Venezuela', flag: '🇻🇪' },
  { value: 'colombia', label: 'Colombia', flag: '🇨🇴' },
  { value: 'panama', label: 'Panamá', flag: '🇵🇦' },
  { value: 'dominicana', label: 'República Dominicana', flag: '🇩🇴' },
];

// Simple Slider Captcha Component
const SliderCaptcha = ({ onVerified, verified, resetKey }) => {
  const [position, setPosition] = useState(0);
  const containerRef = useRef(null);
  const isDragging = useRef(false);
  const threshold = 85;

  // Reset when key changes
  useEffect(() => {
    if (!verified) {
      setPosition(0);
    }
  }, [resetKey, verified]);

  const handleMouseDown = (e) => {
    if (verified) return;
    e.preventDefault();
    isDragging.current = true;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging.current || verified) return;
    
    const container = containerRef.current;
    if (!container) return;
    
    const rect = container.getBoundingClientRect();
    const sliderWidth = 48;
    const maxPosition = rect.width - sliderWidth;
    const newPosition = Math.min(Math.max(0, e.clientX - rect.left - sliderWidth / 2), maxPosition);
    const pct = (newPosition / maxPosition) * 100;
    
    setPosition(pct);
    
    if (pct >= threshold) {
      setPosition(100);
      isDragging.current = false;
      onVerified(true);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }
  }, [verified, onVerified]);

  const handleMouseUp = useCallback(() => {
    if (isDragging.current) {
      setPosition(prev => prev >= threshold ? 100 : 0);
    }
    isDragging.current = false;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseMove]);

  // Touch handlers
  const handleTouchStart = (e) => {
    if (verified) return;
    isDragging.current = true;
  };

  const handleTouchMove = (e) => {
    if (!isDragging.current || verified) return;
    
    const container = containerRef.current;
    if (!container) return;
    
    const touch = e.touches[0];
    const rect = container.getBoundingClientRect();
    const sliderWidth = 48;
    const maxPosition = rect.width - sliderWidth;
    const newPosition = Math.min(Math.max(0, touch.clientX - rect.left - sliderWidth / 2), maxPosition);
    const pct = (newPosition / maxPosition) * 100;
    
    setPosition(pct);
    
    if (pct >= threshold) {
      setPosition(100);
      isDragging.current = false;
      onVerified(true);
    }
  };

  const handleTouchEnd = () => {
    if (isDragging.current && position < threshold) {
      setPosition(0);
    }
    isDragging.current = false;
  };

  return (
    <div className="space-y-2">
      <Label className="text-slate-700 font-medium flex items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-[#2DD4BF]" />
        Verificación de seguridad
      </Label>
      <div 
        ref={containerRef}
        className={`relative h-12 rounded-xl border-2 transition-colors overflow-hidden select-none ${
          verified 
            ? 'bg-[#2DD4BF]/10 border-[#2DD4BF]' 
            : 'bg-slate-50 border-slate-200'
        }`}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        data-testid="slider-captcha"
      >
        {/* Progress fill */}
        <div 
          className={`absolute inset-y-0 left-0 ${
            verified ? 'bg-[#2DD4BF]/20' : 'bg-[#0066CC]/10'
          }`}
          style={{ width: `${position}%`, transition: isDragging.current ? 'none' : 'width 0.2s' }}
        />
        
        {/* Text */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          {verified ? (
            <span className="text-[#2DD4BF] font-semibold flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5" />
              Verificado
            </span>
          ) : (
            <span className="text-slate-400 text-sm">
              Desliza para verificar →
            </span>
          )}
        </div>
        
        {/* Slider thumb */}
        <div
          className={`absolute top-1 bottom-1 w-10 rounded-lg flex items-center justify-center shadow-md ${
            verified 
              ? 'bg-[#2DD4BF] cursor-default' 
              : 'bg-[#0066CC] hover:bg-[#0052a3] cursor-grab active:cursor-grabbing'
          }`}
          style={{ 
            left: `calc(${position}% - ${position * 0.4}px + 4px)`,
            transition: isDragging.current ? 'none' : 'left 0.2s'
          }}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          data-testid="slider-thumb"
        >
          {verified ? (
            <CheckCircle2 className="w-5 h-5 text-white" />
          ) : (
            <div className="flex gap-0.5">
              <div className="w-0.5 h-4 bg-white/60 rounded-full" />
              <div className="w-0.5 h-4 bg-white/60 rounded-full" />
              <div className="w-0.5 h-4 bg-white/60 rounded-full" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    destination: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notRobot, setNotRobot] = useState(false);
  const [sliderVerified, setSliderVerified] = useState(false);
  const [resetKey, setResetKey] = useState(0);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, destination: value }));
  };

  const isVerified = notRobot && sliderVerified;

  const resetForm = () => {
    setFormData({ name: '', email: '', destination: '', message: '' });
    setNotRobot(false);
    setSliderVerified(false);
    setResetKey(prev => prev + 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.destination || !formData.message) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    if (!notRobot) {
      toast.error('Por favor marca la casilla "No soy un robot"');
      return;
    }

    if (!sliderVerified) {
      toast.error('Por favor completa la verificación del slider');
      return;
    }

    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('¡Mensaje enviado! Te contactaremos pronto.');
      resetForm();
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Error al enviar. Intenta de nuevo o contacta por WhatsApp.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section 
      id="contacto" 
      className="relative py-24 md:py-32 bg-white overflow-hidden"
      data-testid="contact-section"
    >
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-[#0066CC]/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#F5A623]/5 rounded-full blur-3xl pointer-events-none" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-flex items-center gap-2 bg-[#CC0000]/10 text-[#CC0000] px-4 py-2 rounded-full text-sm font-semibold mb-4">
            <Mail className="w-4 h-4" />
            Contacto
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            ¿Listo para <span className="text-[#0066CC]">enviar</span>?
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Completa el formulario y te responderemos con una cotización personalizada
          </p>
        </motion.div>
        
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <form 
              onSubmit={handleSubmit}
              className="bg-white rounded-3xl border border-slate-100 shadow-xl p-8 sm:p-10"
              data-testid="contact-form"
            >
              <div className="space-y-6">
                {/* Name */}
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-slate-700 font-medium flex items-center gap-2">
                    <User className="w-4 h-4 text-[#0066CC]" />
                    Nombre completo
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    placeholder="Tu nombre"
                    value={formData.name}
                    onChange={handleChange}
                    className="rounded-xl border-slate-200 focus:border-[#0066CC] focus:ring-[#0066CC]/20 py-6"
                    data-testid="contact-name"
                  />
                </div>
                
                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700 font-medium flex items-center gap-2">
                    <Mail className="w-4 h-4 text-[#0066CC]" />
                    Correo electrónico
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="tu@email.com"
                    value={formData.email}
                    onChange={handleChange}
                    className="rounded-xl border-slate-200 focus:border-[#0066CC] focus:ring-[#0066CC]/20 py-6"
                    data-testid="contact-email"
                  />
                </div>
                
                {/* Destination */}
                <div className="space-y-2">
                  <Label htmlFor="destination" className="text-slate-700 font-medium flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-[#0066CC]" />
                    País de destino
                  </Label>
                  <Select value={formData.destination} onValueChange={handleSelectChange}>
                    <SelectTrigger 
                      className="rounded-xl border-slate-200 focus:border-[#0066CC] focus:ring-[#0066CC]/20 py-6"
                      data-testid="contact-destination"
                    >
                      <SelectValue placeholder="Selecciona un destino" />
                    </SelectTrigger>
                    <SelectContent>
                      {destinations.map((dest) => (
                        <SelectItem 
                          key={dest.value} 
                          value={dest.value}
                          data-testid={`destination-${dest.value}`}
                        >
                          <span className="flex items-center gap-2">
                            <span>{dest.flag}</span>
                            <span>{dest.label}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Message */}
                <div className="space-y-2">
                  <Label htmlFor="message" className="text-slate-700 font-medium flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-[#0066CC]" />
                    Mensaje
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    placeholder="Cuéntanos qué deseas enviar, peso aproximado, etc."
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    className="rounded-xl border-slate-200 focus:border-[#0066CC] focus:ring-[#0066CC]/20 resize-none"
                    data-testid="contact-message"
                  />
                </div>

                {/* Security Section */}
                <div className="space-y-4 pt-2 border-t border-slate-100">
                  <p className="text-xs text-slate-500 text-center pt-2">
                    Completa ambas verificaciones para enviar el formulario
                  </p>
                  
                  {/* Checkbox - No soy un robot */}
                  <label 
                    className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all cursor-pointer ${
                      notRobot 
                        ? 'bg-[#2DD4BF]/5 border-[#2DD4BF]' 
                        : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid="not-robot-container"
                  >
                    <Checkbox 
                      id="not-robot"
                      checked={notRobot}
                      onCheckedChange={(checked) => setNotRobot(checked)}
                      className="h-6 w-6 border-2 data-[state=checked]:bg-[#2DD4BF] data-[state=checked]:border-[#2DD4BF]"
                      data-testid="not-robot-checkbox"
                    />
                    <span className="text-slate-700 font-medium flex-1">
                      No soy un robot
                    </span>
                    {notRobot && (
                      <CheckCircle2 className="w-5 h-5 text-[#2DD4BF]" />
                    )}
                  </label>

                  {/* Slider Captcha */}
                  <SliderCaptcha 
                    key={resetKey}
                    onVerified={setSliderVerified} 
                    verified={sliderVerified}
                    resetKey={resetKey}
                  />
                </div>
                
                {/* Submit */}
                <Button
                  type="submit"
                  disabled={isSubmitting || !isVerified}
                  className={`w-full rounded-full py-6 text-lg font-semibold shadow-lg transition-all ${
                    isVerified 
                      ? 'bg-[#0066CC] hover:bg-[#0052a3] text-white hover:shadow-xl' 
                      : 'bg-slate-300 text-slate-500 cursor-not-allowed'
                  }`}
                  data-testid="contact-submit"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      {isVerified ? 'Enviar Mensaje' : 'Completa la verificación'}
                    </>
                  )}
                </Button>
              </div>
            </form>
          </motion.div>
          
          {/* Mascot Side */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative flex flex-col items-center justify-center"
          >
            {/* Mascot with envelope vibe */}
            <div className="relative">
              <div className="absolute inset-0 bg-[#2DD4BF]/20 rounded-full blur-3xl scale-90 pointer-events-none" />
              <motion.img
                src={MASCOT_URL}
                alt="Mascota - Contáctanos"
                className="relative z-10 w-64 h-64 object-contain"
                animate={{ 
                  y: [0, -15, 0],
                  rotate: [-2, 2, -2]
                }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                data-testid="contact-mascot"
              />
            </div>
            
            {/* Contact info cards */}
            <div className="mt-8 space-y-4 w-full max-w-sm">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="bg-gradient-to-r from-[#F8FAFC] to-white rounded-2xl p-5 border border-slate-100"
              >
                <h4 className="font-bold text-slate-900 mb-1">Respuesta Rápida</h4>
                <p className="text-slate-600 text-sm">
                  Te respondemos en menos de 24 horas con tu cotización
                </p>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="bg-gradient-to-r from-[#F8FAFC] to-white rounded-2xl p-5 border border-slate-100"
              >
                <h4 className="font-bold text-slate-900 mb-1">Sin Compromiso</h4>
                <p className="text-slate-600 text-sm">
                  Cotización gratuita sin obligación de compra
                </p>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                className="bg-gradient-to-r from-[#2DD4BF]/10 to-[#0066CC]/10 rounded-2xl p-5 border border-[#2DD4BF]/20"
              >
                <h4 className="font-bold text-slate-900 mb-1">¿Prefieres WhatsApp?</h4>
                <p className="text-slate-600 text-sm">
                  Haz clic en el botón verde flotante y escríbenos directamente
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
